import { Link, useSearch } from "@tanstack/react-router";
import { useMemo } from "react";
import { CATEGORY_ALL } from "@/components/game-categories/constants";
import useAuth from "@/hooks/use-auth";
import type { ExploreSearchParams } from "@/routes/explore/index";
import useSearchFilters from "../../hooks/use-search-filters";
import useSelectedCategories from "../../hooks/use-selected-categories";
import { DudeIcon } from "./icons";

const LINK_CLASSNAME = "text-berry underline";

interface NoResultsProps {
  hasClosedGames?: boolean;
  onShowClosedGames?: () => void;
}

export default function NoResults({
  hasClosedGames,
  onShowClosedGames,
}: NoResultsProps) {
  const { me } = useAuth();
  const search = useSearch({ strict: false }) as ExploreSearchParams;
  const { appliedFiltersCount, clearUrl } = useSearchFilters();
  const { categoryUrl, primaryCategories, includeLabel } =
    useSelectedCategories();

  const copy = useMemo(() => {
    if (appliedFiltersCount) {
      return (
        <>
          No exact matches. Try{" "}
          <Link className={LINK_CLASSNAME} {...clearUrl()}>
            clearing your filters
          </Link>{" "}
          or adjusting your search area.
        </>
      );
    }

    if (search.query) {
      if (hasClosedGames && onShowClosedGames) {
        return (
          <>
            Nothing was found.{" "}
            <button
              className={`${LINK_CLASSNAME} cursor-pointer`}
              onClick={onShowClosedGames}
              type="button"
            >
              Show closed experiences
            </button>
            ?
          </>
        );
      }

      return (
        <>
          No exact matches for "{search.query}". Want to{" "}
          <a
            className={LINK_CLASSNAME}
            href={
              process.env.SUGGEST_EDIT_URL + (me ? `&user_id=${me.id}` : "")
            }
            rel="noreferrer noopener"
            target="_blank"
          >
            add an experience
          </a>
          ?
        </>
      );
    }

    if (primaryCategories.length) {
      return (
        <>
          No {includeLabel.toLocaleLowerCase()} in the search area, but Morty
          may have{" "}
          <Link className={LINK_CLASSNAME} {...categoryUrl(CATEGORY_ALL)}>
            other experiences nearby
          </Link>
          . 👀
        </>
      );
    }

    return (
      <>
        Nothing was found. Want to{" "}
        <a
          className={LINK_CLASSNAME}
          href={process.env.SUGGEST_EDIT_URL + (me ? `&user_id=${me.id}` : "")}
          rel="noreferrer noopener"
          target="_blank"
        >
          add an experience
        </a>
        ?
      </>
    );
  }, [
    appliedFiltersCount,
    clearUrl,
    hasClosedGames,
    me,
    onShowClosedGames,
    search.query,
    primaryCategories,
    includeLabel,
    categoryUrl,
  ]);

  return (
    <div className="flex flex-col justify-center gap-[49px] pt-5 md:gap-16.5 md:pt-[27px]">
      <p className="text-center text-12 text-platinum leading-[1.2em] md:text-15">
        {copy}
      </p>
      <DudeIcon className="w-[39%]" />
    </div>
  );
}
